<script setup lang="ts">
import type {TbiScheme} from '#server/types/PymentMethodInterface'
import tbiLogo from '../../../../api/public/images/tbi.svg'
const props = withDefaults(defineProps<{amount: number; currency?: string; action?: boolean; disabled?: boolean}>(), {currency: 'EUR'})
const {$api} = useNuxtApp()
const {t, locale} = useI18n()
const open = ref(false)
const loading = ref(false)
const schemes = ref<TbiScheme[]>([])
const error = ref('')
const selected = ref<number>()
let controller: AbortController | undefined
const load = async () => {
    controller?.abort()
    if (!open.value || props.amount <= 0 || props.currency !== 'EUR') return
    const current = new AbortController()
    controller = current
    loading.value = true
    error.value = ''
    schemes.value = []
    try {
        const result = await $api<{schemes: TbiScheme[]}>('/tbi/calculations', {query: {amount: props.amount.toFixed(2), currency: props.currency}, signal: current.signal})
        if (!current.signal.aborted) schemes.value = result.schemes
    } catch { if (!current.signal.aborted) error.value = t('tbi_check_error') }
    finally { if (!current.signal.aborted) loading.value = false }
}
watch([open, () => props.amount, () => props.currency], load)
onBeforeUnmount(() => controller?.abort())
</script>
<template>
    <div v-if="currency === 'EUR' && amount > 0" class="tbi-calculator" :class="{'tbi-calculator--action': action}">
        <button class="tbi-calculator-button" type="button" :disabled="disabled" @click="open = true">
            <img :src="tbiLogo" alt="tbi bank" class="tbi-calculator-logo" /><span>{{ t('tbi_view_installments') }}</span><Icon name="lucide:chevron-right" size="18" />
        </button>
        <el-dialog v-model="open" :title="t('tbi_financing')" width="min(480px, calc(100vw - 24px))" append-to-body>
            <div v-loading="loading" class="tbi-calculator-content">
                <el-alert v-if="error" :title="error" type="warning" :closable="false" show-icon />
                <PaymentsTbiSchemes v-else-if="!loading" v-model="selected" :schemes="schemes" :amount="amount" />
            </div>
        </el-dialog>
    </div>
</template>
<style scoped>
.tbi-calculator { width: 100%; margin-top: 16px; }
.tbi-calculator-button { display: grid; grid-template-columns: 55px 1fr 18px; align-items: center; gap: 12px; width: 100%; min-height: 50px; padding: 10px 16px; border: 1px solid #ffbd87; border-radius: 12px; background: #fffaf5; color: var(--el-text-color-primary); font: inherit; font-size: 14px; cursor: pointer; }
.tbi-calculator-logo { display: block; width: 55px; height: 25px; object-fit: contain; }
.tbi-calculator--action { margin-top: 0; }
.tbi-calculator--action .tbi-calculator-button { min-height: 54px; border-radius: 20px; font-size: 16px; }
.tbi-calculator-button:hover:not(:disabled) { border-color: #ed8e43; background: #fff3e7; }
.tbi-calculator-button:disabled { opacity: .5; cursor: not-allowed; }
.tbi-calculator-button:focus-visible { outline: 2px solid var(--el-color-primary); outline-offset: 3px; }
.tbi-calculator-content { min-height: 120px; }
</style>
